// qaSession.ts — automated per-session QA gate + machine-readable certificate.
//
// A faithful TypeScript port of tools/qa/qa_session.py (the parity oracle).
// Same metrics, thresholds, output schema, warning/blocker strings, and PASS/
// WARN/FAIL verdict model. Pure over a SessionSource (a directory on disk via
// nodeSource.ts, or a browser directory handle via webSource.ts); streams
// tracking.jsonl (sessions can be 100k+ lines / hundreds of MB) — never reads
// it whole. No node imports here — this file also runs in the browser.
//
// Verified field-for-field against the Python via core/test/parity.test.ts.

import { GATES, JOINTS, FLOATS_PER_JOINT, WRIST_IDX } from "./gates.ts";
import { fmtF, fmtPct, parseInt64LE, pyFloat, pyRound } from "./pyhelpers.ts";
import type { SessionSource } from "./source.ts";
import type {
  CertMetrics,
  GateGrade,
  HandStats,
  Provenance,
  QaCertificate,
  TrackingMetrics,
} from "./types.ts";

// ---------------------------------------------------------------------------
// small helpers
// ---------------------------------------------------------------------------

/** How many of the 26 joints are flagged valid in the bitmask. */
function validCount(mask: number): number {
  let m = (Number(mask) | 0) & ((1 << JOINTS) - 1);
  let c = 0;
  while (m) {
    c += m & 1;
    m >>>= 1;
  }
  return c;
}

function bit(mask: number, j: number): number {
  return (Number(mask) >> j) & 1;
}

/** Mean fps, jitter (stddev of inter-frame dt, ms), and dropped-frame count. */
function fpsFromTimestamps(ts: number[]): [number | null, number | null, number] {
  if (ts.length < 3) return [null, null, 0];
  const dts: number[] = [];
  for (let i = 0; i < ts.length - 1; i++) {
    if (ts[i + 1] > ts[i]) dts.push((ts[i + 1] - ts[i]) / 1e9);
  }
  if (dts.length === 0) return [null, null, 0];
  const meanDt = dts.reduce((a, b) => a + b, 0) / dts.length;
  const fps = meanDt > 0 ? 1.0 / meanDt : null;
  const varr = dts.reduce((a, d) => a + (d - meanDt) ** 2, 0) / dts.length;
  const jitterMs = Math.sqrt(varr) * 1000.0;
  const sorted = [...dts].sort((a, b) => a - b);
  const med = sorted[Math.floor(sorted.length / 2)];
  const drops = med > 0 ? dts.filter((d) => d > 1.8 * med).length : 0;
  return [fps, jitterMs, drops];
}

/** PASS / WARN / FAIL for a value against two thresholds (NA when value absent). */
function grade(
  value: number | null | undefined,
  warn: number,
  fail: number,
  higherIsBetter = true,
): GateGrade {
  if (value === null || value === undefined) return "NA";
  if (higherIsBetter) {
    if (value < fail) return "FAIL";
    if (value < warn) return "WARN";
    return "PASS";
  }
  if (value > fail) return "FAIL";
  if (value > warn) return "WARN";
  return "PASS";
}

function worst(verdicts: GateGrade[]): GateGrade {
  const order: Record<GateGrade, number> = { FAIL: 3, WARN: 2, PASS: 1, NA: 0 };
  if (verdicts.length === 0) return "NA";
  return verdicts.reduce((acc, v) => (order[v] > order[acc] ? v : acc), "NA" as GateGrade);
}

// ---------------------------------------------------------------------------
// the streaming pass over tracking.jsonl — the only expensive part
// ---------------------------------------------------------------------------
interface HandAcc {
  full_valid: number;
  valid_frac_sum: number;
  glitches: number;
  pairs: number;
  prev_wrist: number[] | null;
  prev_t: number | null;
}

async function scanTracking(src: SessionSource): Promise<TrackingMetrics | null> {
  if (!(await src.exists("tracking.jsonl"))) return null;

  let frames = 0;
  let camLok = 0;
  let camRok = 0;
  let headOk = 0;
  let bodyPresent = false;
  let bodyValid = 0;
  const hands: Record<"fltL" | "fltR", HandAcc> = {
    fltL: { full_valid: 0, valid_frac_sum: 0, glitches: 0, pairs: 0, prev_wrist: null, prev_t: null },
    fltR: { full_valid: 0, valid_frac_sum: 0, glitches: 0, pairs: 0, prev_wrist: null, prev_t: null },
  };

  for await (const rawLine of src.lines("tracking.jsonl")) {
    const line = rawLine.trim();
    if (!line) continue;
    let o: any;
    try {
      o = JSON.parse(line);
    } catch {
      continue;
    }
    frames++;
    const t: number | null | undefined = o.t;
    if (o.head && o.head.length) headOk++;
    if (o.camL != null) camLok++;
    if (o.camR != null) camRok++;
    if (o.body != null) {
      bodyPresent = true;
      const bv = o.body.v;
      // body validity is a 3x uint32 array; treat any nonzero as tracking
      if (bv && bv.some((x: unknown) => parseInt(String(x), 10) !== 0)) bodyValid++;
    }

    for (const h of ["fltL", "fltR"] as const) {
      const hd = o[h];
      if (!hd) continue;
      const vmask = hd.v ?? 0;
      const vc = validCount(vmask);
      hands[h].valid_frac_sum += vc / JOINTS;
      if (vc === JOINTS) hands[h].full_valid++;
      // wrist teleport detection across consecutive valid frames
      const p: number[] | undefined = hd.p;
      const wristValid = bit(vmask, WRIST_IDX);
      if (p && wristValid && p.length >= (WRIST_IDX + 1) * FLOATS_PER_JOINT) {
        const base = WRIST_IDX * FLOATS_PER_JOINT;
        const w = [p[base], p[base + 1], p[base + 2]];
        const pv = hands[h].prev_wrist;
        const pt = hands[h].prev_t;
        if (pv != null && pt != null && t != null && t > pt) {
          const dt = (t - pt) / 1e9;
          const d = Math.sqrt((w[0] - pv[0]) ** 2 + (w[1] - pv[1]) ** 2 + (w[2] - pv[2]) ** 2);
          const spd = dt > 0 ? d / dt : 0.0;
          hands[h].pairs++;
          if (spd > GATES.wrist_speed_gate_mps) hands[h].glitches++;
        }
        hands[h].prev_wrist = w;
        hands[h].prev_t = t ?? null;
      } else {
        hands[h].prev_wrist = null;
        hands[h].prev_t = null;
      }
    }
  }

  const handStats = (h: "fltL" | "fltR"): HandStats => ({
    uptime: frames ? hands[h].full_valid / frames : 0,
    mean_valid_frac: frames ? hands[h].valid_frac_sum / frames : 0,
    glitch_rate: hands[h].pairs ? hands[h].glitches / hands[h].pairs : 0.0,
    glitches: hands[h].glitches,
  });

  return {
    frames,
    head_uptime: frames ? headOk / frames : 0,
    camL_uptime: frames ? camLok / frames : 0,
    camR_uptime: frames ? camRok / frames : 0,
    body_present: bodyPresent,
    body_uptime: frames && bodyPresent ? bodyValid / frames : null,
    hands: { fltL: handStats("fltL"), fltR: handStats("fltR") },
  };
}

// ---------------------------------------------------------------------------
// certificate assembly (= qa_session.assess)
// ---------------------------------------------------------------------------

/** Read an int64-LE .timestamps sidecar ([] when absent, like the oracle). */
async function readTimestamps(src: SessionSource, rel: string): Promise<number[]> {
  if (!(await src.exists(rel))) return [];
  return parseInt64LE(await src.readBytes(rel));
}

export async function assessSource(src: SessionSource): Promise<QaCertificate> {
  const name = src.name;
  const gates: Record<string, GateGrade> = {};
  const metrics: CertMetrics = {};
  const warnings: string[] = [];
  const blockers: string[] = [];
  const cert: QaCertificate = {
    session_id: name,
    qa_schema_version: 1,
    gates,
    metrics,
    provenance: {},
    warnings,
    blockers,
  };

  if (!(await src.exists("meta.json"))) {
    cert.verdict = "FAIL";
    blockers.push("no meta.json — not a session directory");
    return cert;
  }
  const meta: any = JSON.parse(await src.readText("meta.json"));

  // ---- duration -------------------------------------------------------
  let dur: number | null = null;
  if (meta.start_elapsed_ns && meta.end_elapsed_ns) {
    dur = (meta.end_elapsed_ns - meta.start_elapsed_ns) / 1e9;
  }
  metrics.duration_s = dur ? pyRound(dur, 2) : null;
  gates.duration = grade(dur, GATES.min_duration_s, GATES.min_duration_s);

  // ---- video sync (frame counts + fps from timestamps) ----------------
  const lts = await readTimestamps(src, "left.timestamps");
  const rts = await readTimestamps(src, "right.timestamps");
  const [lf, lj, ldrop] = fpsFromTimestamps(lts);
  const [rf, rj, rdrop] = fpsFromTimestamps(rts);
  const skew = Math.abs(lts.length - rts.length);
  const fpsCandidates = [lf, rf].filter((x): x is number => x != null && x !== 0);
  const fpsMin = fpsCandidates.length ? Math.min(...fpsCandidates) : null;
  // EGO-149: the fps target comes from the session's own meta — the AE pin
  // stamps the fixed [fps,fps] range per eye, so a 30 fps task is graded
  // against 30, not the global default. Non-positive ranges ([0,0] = the eye
  // never streamed, e.g. mono config) don't vote; no ranges at all (legacy
  // sessions) fall back to GATES.fps_target.
  const metaTargets = [meta.left_fps_target_range, meta.right_fps_target_range]
    .filter((r: any): r is number[] =>
      Array.isArray(r) && r.length >= 2 && typeof r[1] === "number" && r[1] > 0)
    .map((r) => r[1]);
  const target = metaTargets.length ? Math.max(...metaTargets) : GATES.fps_target;
  const fpsGrade = fpsMin
    ? grade(fpsMin, GATES.fps_warn_frac * target, GATES.fps_fail_frac * target)
    : "NA";
  // EGO-149: a right eye configured OFF (mono capture: [0,0] target range,
  // zero frames, no sidecar) is not a sync failure — grade NA. A right eye
  // configured on that produced nothing stays a FAIL.
  const rRange: any = meta.right_fps_target_range;
  const rightConfiguredOff =
    Array.isArray(rRange) && rRange.length >= 2 &&
    !(typeof rRange[1] === "number" && rRange[1] > 0) &&
    !((meta.right_frames ?? 0) > 0) &&
    rts.length === 0;
  const skewGrade = rightConfiguredOff
    ? "NA"
    : grade(skew, GATES.lr_frame_skew_max, GATES.lr_frame_skew_max * 3, false);
  metrics.video = {
    left_frames: lts.length,
    right_frames: rts.length,
    lr_skew: skew,
    left_fps: lf ? pyRound(lf, 2) : null,
    right_fps: rf ? pyRound(rf, 2) : null,
    left_jitter_ms: lj ? pyRound(lj, 2) : null,
    right_jitter_ms: rj ? pyRound(rj, 2) : null,
    timestamp_drops: ldrop + rdrop,
    fps_target: target,
  };
  gates.video_fps = fpsGrade;
  gates.video_lr_sync = skewGrade;
  if (skewGrade === "FAIL") {
    blockers.push(`left/right frame skew ${skew} > ${GATES.lr_frame_skew_max * 3}`);
  }

  // ---- tracking (the heavy streaming pass) ----------------------------
  const trk = await scanTracking(src);
  if (trk) {
    metrics.tracking = trk;
    for (const h of ["fltL", "fltR"] as const) {
      const up = trk.hands[h].uptime;
      const gl = trk.hands[h].glitch_rate;
      const ug = grade(up, GATES.hand_uptime_warn, GATES.hand_uptime_fail);
      const gg = grade(gl, GATES.glitch_rate_warn, GATES.glitch_rate_fail, false);
      gates[`hand_${h}_uptime`] = ug;
      gates[`hand_${h}_glitch`] = gg;
      if (ug === "FAIL") {
        blockers.push(
          `${h} hand uptime ${fmtPct(up, 0)} < ${fmtPct(GATES.hand_uptime_fail, 0)}`,
        );
      }
      if (gg === "WARN") {
        warnings.push(
          `${h} wrist teleports ${fmtPct(gl, 1)} of frames ` +
            `(>${pyFloat(GATES.wrist_speed_gate_mps)} m/s) — filter before training use`,
        );
      }
    }
    // camera true-pose coverage (the metric that separates us from GoPro rigs)
    const camu = Math.min(trk.camL_uptime, trk.camR_uptime);
    const cg = grade(camu, GATES.cam_uptime_warn, GATES.cam_uptime_fail);
    gates.camera_world_pose = cg;
    if (cg !== "PASS") {
      warnings.push(`true camera world pose present on only ${fmtPct(camu, 0)} of frames`);
    }
    // body (only if present)
    if (trk.body_present) {
      gates.body_uptime = grade(
        trk.body_uptime,
        GATES.body_uptime_warn,
        GATES.body_uptime_warn * 0.6,
      );
    }
  }

  // ---- depth ----------------------------------------------------------
  if (await src.exists("depth.json")) {
    const dj: any = JSON.parse(await src.readText("depth.json"));
    const dropped = dj.dropped ?? 0;
    metrics.depth = { frames: dj.frames, dropped, resolution: [dj.width, dj.height] };
    gates.depth = grade(dropped, GATES.depth_drop_warn, GATES.depth_drop_warn * 10, false);
  }

  // ---- audio / imu presence ------------------------------------------
  if (meta.audio_enabled && meta.audio) {
    const a = meta.audio;
    const adur = (a.frames ?? 0) / (a.sample_rate ?? 48000);
    metrics.audio = { present: true, duration_s: pyRound(adur, 2) };
    if (dur && adur < 0.8 * dur) {
      warnings.push(`audio covers ${fmtF(adur, 1)}s of a ${fmtF(dur, 1)}s session`);
    }
  } else {
    metrics.audio = { present: false };
  }
  if (meta.imu_enabled && meta.imu) {
    const ev = meta.imu.events ?? 0;
    const rate = dur ? ev / dur : null;
    metrics.imu = { present: true, events: ev, rate_hz: rate != null ? pyRound(rate, 1) : null };
  } else {
    metrics.imu = { present: false };
  }

  // ---- provenance: what calibration backs this session ----------------
  const has = (k: string) => Object.prototype.hasOwnProperty.call(meta, k);
  const provenance: Provenance = {
    schema_version: has("schema_version") ? meta.schema_version : null,
    intrinsics_source: has("left_intrinsics_source") ? meta.left_intrinsics_source : "api",
    distortion_calibrated: (meta.left_distortion ?? null) !== null,
    extrinsics_source: has("left_pose_source")
      ? meta.left_pose_source
      : trk && trk.camL_uptime > 0
        ? "per-frame-mruk"
        : "api-static",
    time_offset_ms: has("pose_time_offset_ms") ? meta.pose_time_offset_ms : null,
    time_offset_source: has("pose_time_offset_source") ? meta.pose_time_offset_source : null,
  };
  cert.provenance = provenance;
  if (provenance.time_offset_ms == null) {
    warnings.push("pose_time_offset_ms not stamped — run estimate_time_offset.py");
  }
  if (!provenance.distortion_calibrated) {
    warnings.push("lens distortion uncalibrated — pinhole model assumed");
  }

  // ---- overall verdict ------------------------------------------------
  const overall = worst(Object.values(gates));
  // WARN-level gates don't block delivery, only FAIL does
  cert.verdict = overall === "FAIL" ? "FAIL" : "PASS";
  cert.verdict_detail = overall;
  return cert;
}

// The node entry point over a directory path — `assess(sessionDir)` — and
// `isSessionDir` live in nodeSource.ts: importing them from here would drag
// node:fs into browser bundles (esbuild resolves imports before tree-shaking).
